/**
 * On-truck decal preview — the PDP's answer to "will it look right on my door".
 *
 * Design decisions, deliberate:
 *
 *   - No perspective controls. The prototype this came from had sliders for
 *     skew, rotation and position because it did not know the door geometry.
 *     The geometry ships in mmf-truck-preview.json (from the asset pack's
 *     mapping file), so placement is exact and the customer only ever types
 *     their text. Fewer controls, zero ways to make it look wrong.
 *
 *   - Driven by the ORDER FIELDS, not its own inputs. The preview listens to
 *     the same properties[...] fields that submit with the cart, so what you
 *     preview is literally what you ordered. Vinyl colour follows the selected
 *     Color variant the same way.
 *
 *   - Truck paint swatches exist because the federal rule requires the marking
 *     to "contrast sharply" with the background. White-on-white is a mistake
 *     this preview makes visible before purchase instead of after delivery.
 *
 *   - Lazy: nothing loads until the section approaches the viewport. Four webp
 *     layers total ~130KB and none of them touch LCP.
 *
 * The door quad has parallel vertical edges, so the mapping is affine and a
 * single setTransform projects the text plane onto the door exactly — no
 * homography, no approximation.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-truck-preview]");
  if (!root) return;

  var canvas = root.querySelector("canvas");
  var ctx = canvas.getContext("2d");
  var assetBase = root.getAttribute("data-asset-base") || "";
  var fallbackLines = [];
  try {
    fallbackLines = JSON.parse(root.getAttribute("data-fallback-lines") || "[]");
  } catch (e) { /* leave empty */ }

  var config = null;
  var layers = {};
  var truckPaint = null; // set from config default after load
  var started = false;

  function assetUrl(name) {
    // Swap the filename but KEEP the ?v= cache-buster. Dropping it served a
    // stale config JSON from the CDN after the door geometry was corrected —
    // the base attribute was new, the fetched sibling was old, and the decal
    // rendered with last deploy's quad. The v param is per-deploy, so the base
    // asset's value is valid for every sibling.
    var parts = assetBase.split("?");
    return parts[0].replace(/[^\/]+$/, name) + (parts[1] ? "?" + parts[1] : "");
  }

  /* ------------------------------------------------------------ inputs */

  function currentLines() {
    var fields = document.querySelectorAll('input[name^="properties["]');
    var lines = [];
    fields.forEach(function (f) {
      var v = f.value.trim();
      if (!v) return;
      var name = (f.name.match(/properties\[(.+)\]/) || [])[1] || "";
      // Present the USDOT/state numbers the way they are cut, prefixed.
      if (/^usdot/i.test(name) && !/usdot/i.test(v)) v = "USDOT " + v;
      if (/^mc number/i.test(name) && !/^mc/i.test(v)) v = "MC " + v;
      if (/^kyu/i.test(name) && !/^kyu/i.test(v)) v = "KYU " + v;
      if (/^ca number/i.test(name) && !/^ca/i.test(v)) v = "CA " + v;
      if (/^txdmv/i.test(name) && !/txdmv/i.test(v)) v = "TxDMV " + v;
      if (/gross vehicle weight/i.test(name)) v = "GVW " + v.replace(/^gvw\s*/i, "") + " LBS";
      lines.push(v.toUpperCase());
    });
    if (lines.length === 0) lines = fallbackLines.map(function (l) { return l.toUpperCase(); });
    return lines.slice(0, config ? config.door.max_lines : 5);
  }

  function selectedVinylHex() {
    var checked = document.querySelector(
      'variant-selects input[type="radio"]:checked, fieldset input[type="radio"]:checked'
    );
    var picked = null;
    document.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
      if (/color/i.test(r.name)) picked = r.value;
    });
    if (!picked && checked) picked = null;
    var map = config.vinyl_colors;
    return (picked && map[picked]) || map["Black"];
  }

  function usingSample() {
    var fields = document.querySelectorAll('input[name^="properties["]');
    for (var i = 0; i < fields.length; i++) if (fields[i].value.trim()) return false;
    return fields.length > 0 || fallbackLines.length === 0 ? fields.length > 0 : true;
  }

  /* ------------------------------------------------------------ render */

  function render() {
    if (!config || !layers.base) return;
    var W = config.canvas.width;
    var H = config.canvas.height;
    canvas.width = W;
    canvas.height = H;

    ctx.clearRect(0, 0, W, H);

    // Layer stack per the asset manifest: shadow, base, tinted paint, text, glare.
    ctx.drawImage(layers.shadow, 0, 0, W, H);
    ctx.drawImage(layers.base, 0, 0, W, H);

    if (truckPaint && layers.paint) {
      var off = document.createElement("canvas");
      off.width = W;
      off.height = H;
      var octx = off.getContext("2d");
      octx.drawImage(layers.paint, 0, 0, W, H);
      octx.globalCompositeOperation = "source-in";
      octx.fillStyle = truckPaint;
      octx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "multiply";
      ctx.drawImage(off, 0, 0);
      ctx.globalCompositeOperation = "source-over";
    }

    drawLettering();

    ctx.globalCompositeOperation = "screen";
    ctx.drawImage(layers.glare, 0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
  }

  function drawLettering() {
    var lines = currentLines();
    if (!lines.length) return;

    var q = config.door.quad;
    var margin = config.door.inner_margin;

    // Text plane dimensions in door-local space.
    var planeW = Math.hypot(q.top_right.x - q.top_left.x, q.top_right.y - q.top_left.y);
    var planeH = Math.hypot(q.bottom_left.x - q.top_left.x, q.bottom_left.y - q.top_left.y);

    var off = document.createElement("canvas");
    off.width = Math.round(planeW);
    off.height = Math.round(planeH);
    var octx = off.getContext("2d");

    var usable = planeW - margin * 2;
    var gap = 1.32;
    var font = function (px) {
      return "700 " + px + 'px "Arial Narrow", Impact, "Helvetica Neue", Arial, sans-serif';
    };

    // Largest uniform size where the widest line fits and the block fits.
    var size = Math.floor(planeH / (lines.length * gap));
    for (;;) {
      octx.font = font(size);
      var widest = 0;
      lines.forEach(function (l) {
        widest = Math.max(widest, octx.measureText(l).width);
      });
      if ((widest <= usable && size * gap * lines.length <= planeH - margin) || size <= 10) break;
      size -= 1;
    }

    octx.font = font(size);
    octx.textAlign = "center";
    octx.textBaseline = "middle";
    octx.fillStyle = selectedVinylHex();

    // Reflective White gets a faint halo — the only finish effect kept from the
    // prototype, because it communicates a real product difference.
    var picked = null;
    document.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
      if (/color/i.test(r.name)) picked = r.value;
    });
    if (picked === "Reflective White") {
      octx.shadowColor = "rgba(255,255,235,0.9)";
      octx.shadowBlur = size * 0.25;
    }

    var blockH = lines.length * size * gap;
    var y = (planeH - blockH) / 2 + (size * gap) / 2;
    lines.forEach(function (l) {
      octx.fillText(l, planeW / 2, y);
      y += size * gap;
    });

    // Affine map of the text plane onto the door quad: origin at top-left,
    // x-basis along the top edge, y-basis along the left edge.
    var ax = (q.top_right.x - q.top_left.x) / planeW;
    var ay = (q.top_right.y - q.top_left.y) / planeW;
    var bx = (q.bottom_left.x - q.top_left.x) / planeH;
    var by = (q.bottom_left.y - q.top_left.y) / planeH;

    ctx.save();
    ctx.setTransform(ax, ay, bx, by, q.top_left.x, q.top_left.y);
    ctx.globalAlpha = 0.96;
    ctx.drawImage(off, 0, 0);
    ctx.restore();

    var sampleBadge = root.querySelector("[data-sample-badge]");
    if (sampleBadge) sampleBadge.hidden = !usingSample();
  }

  /* ------------------------------------------------------------- init */

  function boot() {
    if (started) return;
    started = true;

    fetch(assetUrl("mmf-truck-preview.json"))
      .then(function (r) {
        if (!r.ok) throw new Error("config " + r.status);
        return r.json();
      })
      .then(function (cfg) {
        config = cfg;
        truckPaint = cfg.truck_paints[0].hex;
        buildSwatches(cfg.truck_paints);

        var names = ["shadow", "base", "paint", "glare"];
        return Promise.all(
          names.map(function (n) {
            return new Promise(function (resolve, reject) {
              var img = new Image();
              img.onload = function () {
                layers[n] = img;
                resolve();
              };
              img.onerror = reject;
              img.src = assetUrl(cfg.layers[n]);
            });
          })
        );
      })
      .then(function () {
        render();
        document.addEventListener("input", function (e) {
          if (e.target && /^properties\[/.test(e.target.name || "")) render();
        });
        document.addEventListener("change", function (e) {
          if (e.target && /color/i.test(e.target.name || "")) render();
        });
      })
      .catch(function () {
        // A broken preview must never break a working product page.
        root.hidden = true;
      });
  }

  function buildSwatches(paints) {
    var holder = root.querySelector("[data-paint-swatches]");
    if (!holder) return;
    paints.forEach(function (p, i) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "mmf-paint-swatch";
      b.style.backgroundColor = p.hex;
      b.setAttribute("aria-label", "Truck colour: " + p.name);
      b.setAttribute("aria-pressed", i === 0 ? "true" : "false");
      b.addEventListener("click", function () {
        truckPaint = p.hex;
        holder.querySelectorAll("button").forEach(function (x) {
          x.setAttribute("aria-pressed", "false");
        });
        b.setAttribute("aria-pressed", "true");
        render();
      });
      holder.appendChild(b);
    });
  }

  /* Lazy, but guaranteed.

     IntersectionObserver is the fast path, not the only path: its callbacks are
     tied to rendering steps, and a tab that is not compositing (background tab,
     some embedded webviews, automation) never fires them. boot() is idempotent,
     so belt and braces cost nothing: whichever of these happens first wins, and
     the timer ensures the preview exists even if none of the others ever fire.
     ~130KB of layers after a 4s post-load delay does not touch LCP. */
  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            io.disconnect();
            boot();
          }
        });
      },
      { rootMargin: "400px" }
    );
    io.observe(root);
  }
  window.addEventListener("scroll", boot, { once: true, passive: true });
  window.addEventListener("pointerdown", boot, { once: true });
  if (document.readyState === "complete") {
    setTimeout(boot, 4000);
  } else {
    window.addEventListener("load", function () {
      setTimeout(boot, 4000);
    });
  }
})();
